import { DEFAULT_OPACITY } from "constants/chart.constants";
import { SeriesOption } from "echarts";
import { EvaChartStates } from "types/factory";

import { getProportionalPointSize } from "hooks/charts/useSeriesStyle";

import { getEffectiveSeriesColor } from "../../translators/getEffectiveSeriesColor";
import {
  BLUR_OPACITY,
  DEFAULT_WIDTH,
  DEFAULT_Z_INDEX,
  HIGHLIGHTED_Z_INDEX,
  SELECTED_WELL_COLOR,
  SELECTED_WELL_Z_INDEX,
  STATISTICS_Z_INDEX,
  TYPEWELL_Z_INDEX
} from "../../translators/constants";
import {
  computeSelectionStates,
  computeSyncedWellState,
  isStatisticsLabel
} from "../../translators/utils";

export function baseLineWithSyncHighlightedSeries(states: Partial<EvaChartStates>) {
  const {
    series,
    hoverLegendItem,
    hoverLegendGroup,
    hoverLegendItemUnfiltered,
    hoverLegendIsMultiProductChart,
    attentionWells,
    selectedGroups = [],
    hoverLegendId,
    syncWells,
    sum,
    chartOptions,
    getSeriesStyle,
    getHighlightedSeriesStyle,
    multiProduct
  } = states;

  const isMultiProductActive = multiProduct?.active ?? false;
  // When multi-product is active, hoverLegendItem may have the product suffix stripped)
  // so use the unfiltered hover value (pre-strip) for exact-match comparisons to highlight
  // When highlighting from regular chart, to match multiproduct, still needs to be hoverLegendItem
  const effectiveHoverLegendItem =
    isMultiProductActive && hoverLegendIsMultiProductChart
      ? hoverLegendItemUnfiltered || hoverLegendItem
      : hoverLegendItem;

  const chartOptionSeries = (chartOptions?.series as SeriesOption[]) ?? [];

  if (!Array.isArray(chartOptionSeries || series?.length === 0)) {
    return {};
  }

  const isSpaghetti = sum?.active;
  const hasTypewellSeries = chartOptionSeries.some((s) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore - kind is a custom property we add to series
    const { kind } = s;
    return kind === "typewell";
  });

  // stop fade out all series if we have typewell/working forecast series
  const isOpaque = !(
    effectiveHoverLegendItem ||
    selectedGroups.length > 0 ||
    (syncWells?.activeWells?.length > 0 &&
      syncWells?.isSync &&
      syncWells?.highlightSyncedWell !== false &&
      isSpaghetti &&
      !hasTypewellSeries)
  );

  return chartOptionSeries.map((s, index) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const { name = "", id, kind } = s;
    const style = series[index]?.style;
    const groupTitle = series[index]?.groupTitle ?? "";
    const label = name as string;
    const isStatisticsSeries = isStatisticsLabel(label);

    if (kind === "typewell") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const originalId = s.originalId ?? "";
      const isHovered = hoverLegendId === originalId;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const typeWellColor = s.line?.originalColor ?? "#000";
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const typeWellLineWidth = s.line?.originalWidth ?? DEFAULT_WIDTH;
      return {
        id,
        itemStyle: {
          color: typeWellColor
        },
        lineStyle: {
          width: isHovered ? typeWellLineWidth + DEFAULT_WIDTH : typeWellLineWidth,
          color: typeWellColor,
          opacity: isOpaque ? DEFAULT_OPACITY : isHovered ? DEFAULT_OPACITY : BLUR_OPACITY
        },
        z: isHovered ? SELECTED_WELL_Z_INDEX : TYPEWELL_Z_INDEX
      };
    }

    // find corresponding series from chartOptionsSeries by comparing label with name from chartOptions
    const chartOptionSeriesItemData = Array.isArray(chartOptionSeries)
      ? chartOptionSeries.find((item) => item?.name === label)?.data
      : undefined;

    const isPoint =
      Array.isArray(chartOptionSeriesItemData) && chartOptionSeriesItemData.length === 1;

    const { isSelectedWell, isHovered } = computeSelectionStates({
      hoverLegendItem: effectiveHoverLegendItem,
      hoverLegendGroup,
      attentionWells,
      label,
      groupTitle,
      selectedGroups,
      isMultiProductActive
    });

    const { isSyncedWell } = computeSyncedWellState({
      label,
      syncWells,
      isSpaghetti,
      hoverLegendItem: effectiveHoverLegendItem
    });
    const { opacity, thickness } = getSeriesStyle(label, style);
    const { opacity: highlightOpacity, thickness: highlightThickness } =
      getHighlightedSeriesStyle(label);

    const effectiveColor = getEffectiveSeriesColor(series[index], multiProduct);

    return {
      id,
      itemStyle: {
        color: isSelectedWell || isSyncedWell ? "#000" : effectiveColor
      },
      lineStyle: {
        width: isHovered || isSyncedWell ? highlightThickness : thickness,
        color: isSelectedWell || isSyncedWell ? SELECTED_WELL_COLOR : effectiveColor,
        // do I need to reapply type?
        opacity: isOpaque
          ? opacity
          : isHovered || isSyncedWell
          ? highlightOpacity
          : BLUR_OPACITY
      },

      z:
        isSelectedWell || isSyncedWell
          ? SELECTED_WELL_Z_INDEX
          : isHovered
          ? HIGHLIGHTED_Z_INDEX
          : isStatisticsSeries
          ? STATISTICS_Z_INDEX
          : DEFAULT_Z_INDEX,
      ...(isPoint
        ? {
            symbolSize:
              isHovered || isSyncedWell
                ? getProportionalPointSize(highlightThickness)
                : getProportionalPointSize(thickness),
            symbol: "circle",
            itemStyle: {
              color: isSelectedWell || isSyncedWell ? "#000" : effectiveColor,
              opacity: isOpaque
                ? opacity
                : isHovered || isSyncedWell
                ? highlightOpacity
                : BLUR_OPACITY
            }
          }
        : {})
    };
  });
}
